import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getIdToken } from '@/services/auth'
import { getWorkflows } from '@/services/api'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { allowsOrderDataClear } from '@/constants/deployment'
import { useIsRootOrganization } from '@/features/admin/useIsRootOrganization'
import { MOCK_SEED_IDS } from '@/constants/mockSeedIds'
import type { WorkflowResetResult } from '@/types/workflowReset'

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || ''

const { workflow: WF } = MOCK_SEED_IDS

type MigrationEntry = {
  version: number
  name: string
  status: 'applied' | 'pending'
}

type MigrationStatus = {
  recorded_version: number | null
  dirty: boolean
  assumed_baseline_version: number
  effective_version: number
  migrations: MigrationEntry[]
  pending_count: number
}

type ApiResponse<T> = {
  data: T
  message?: string
}

const PRESETS: { group: string; endpoints: { label: string; path: string }[] }[] = [
  {
    group: 'Auth',
    endpoints: [{ label: 'Current user', path: '/v1/users/me' }],
  },
  {
    group: 'Workflows',
    endpoints: [
      { label: 'List workflows', path: '/v1/workflows/' },
      { label: 'Awaiting validation', path: `/v1/workflows/${WF}/todo` },
      { label: 'Pending', path: `/v1/workflows/${WF}/pending` },
      { label: 'Done', path: `/v1/workflows/${WF}/done` },
      { label: 'OC settings', path: `/v1/workflows/${WF}/settings` },
    ],
  },
]

function formatResponse(status: number, raw: string): string {
  let body = raw
  try {
    body = JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    /* not JSON */
  }
  return `Status: ${status}\n\n${body}`
}

/**
 * What the Reset workflow section reports back. A failed run trigger returns 200 with
 * a warning rather than an error status, so the raw body is the only place the
 * difference shows — hence this summary sitting above the response pane.
 */
type WorkflowResetSummary = {
  status: number
  askedForRun: boolean
  error?: string
  runStarted?: boolean
  startBlock?: string
  mailMovedBack?: number
  purchaseOrders?: string[]
  bucketObjectsDeleted?: number
  warnings?: string[]
}

function summarizeWorkflowReset(status: number, raw: string, askedForRun: boolean): WorkflowResetSummary {
  let parsed: (ApiResponse<WorkflowResetResult> & { error?: string }) | null = null
  try {
    parsed = JSON.parse(raw) as ApiResponse<WorkflowResetResult> & { error?: string }
  } catch {
    /* not JSON — fall through to the raw body below */
  }

  if (status < 200 || status >= 300) {
    return {
      status,
      askedForRun,
      error: parsed?.error || raw.trim().slice(0, 300) || 'The request failed with no body.',
    }
  }
  if (!parsed?.data) {
    return { status, askedForRun, error: 'The reset returned no data.' }
  }

  const data = parsed.data
  return {
    status,
    askedForRun,
    runStarted: data.run_started,
    startBlock: data.start_block,
    mailMovedBack: data.mail_moved_back,
    purchaseOrders: data.purchase_orders_reset,
    bucketObjectsDeleted: data.bucket_objects_deleted,
    warnings: data.warnings,
  }
}

const DEFAULT_ERP_ORDER_NUMBER = 'PO-2026-00001'
const canClearOrderData = allowsOrderDataClear()

export function DeveloperToolsPage() {
  const { isRoot } = useIsRootOrganization()
  // The backend allows workflow reset in every stage, for the demo organization
  // only. In production that is FrontFlow, the root organization, so nobody
  // else is shown a button they would only get a 403 from.
  const canResetWorkflow = canClearOrderData || isRoot
  const [endpoint, setEndpoint] = useState('/v1/users/me')
  const [responseText, setResponseText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [activePreset, setActivePreset] = useState('/v1/users/me')
  const [erpWorkflowId, setErpWorkflowId] = useState(WF)
  const [erpOrderNumber, setErpOrderNumber] = useState(DEFAULT_ERP_ORDER_NUMBER)
  const [migrationStatus, setMigrationStatus] = useState<MigrationStatus | null>(null)
  const [migrationError, setMigrationError] = useState('')
  const [resetWorkflowId, setResetWorkflowId] = useState('')
  const [initiateResetRun, setInitiateResetRun] = useState(true)
  const [resetSummary, setResetSummary] = useState<WorkflowResetSummary | null>(null)

  // Already cached app-wide by the sidebar, so this costs no extra request.
  const { data: workflows = [] } = useQuery({ queryKey: ['workflows'], queryFn: getWorkflows })

  async function executeRequest(
    path: string,
    method = 'GET',
  ): Promise<{ status: number; text: string }> {
    const normalizedPath = path.trim().startsWith('/') ? path.trim() : `/${path.trim()}`
    const token = await getIdToken()
    const response = await fetch(`${API_BASE_URL}${normalizedPath}`, {
      method,
      headers: { Authorization: `Bearer ${token}` },
    })
    const text = await response.text()
    return { status: response.status, text }
  }

  async function fireRequest(path: string, method = 'GET') {
    if (method === 'GET') {
      const normalizedPath = path.trim().startsWith('/') ? path.trim() : `/${path.trim()}`
      setEndpoint(normalizedPath)
      setActivePreset(normalizedPath)
    }
    setIsLoading(true)
    setResponseText('')
    try {
      if (!API_BASE_URL) {
        throw new Error('API base URL is not configured (VITE_API_GATEWAY_URL).')
      }
      const { status, text } = await executeRequest(path, method)
      setResponseText(formatResponse(status, text))
    } catch (error) {
      setResponseText(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  async function runDatabaseStep(
    path: string,
    method: 'GET' | 'POST',
    label: string,
  ): Promise<boolean> {
    setResponseText(`Running: ${label}…`)
    const { status, text } = await executeRequest(path, method)
    setResponseText(`${label}\n${formatResponse(status, text)}`)
    return status >= 200 && status < 300
  }

  async function checkMigrationStatus() {
    setIsLoading(true)
    setMigrationError('')
    setResponseText('Checking migration status…')
    try {
      if (!API_BASE_URL) {
        throw new Error('API base URL is not configured (VITE_API_GATEWAY_URL).')
      }
      const { status, text } = await executeRequest('/v1/database/migrations/status', 'GET')
      setResponseText(`Migration status\n${formatResponse(status, text)}`)
      if (status < 200 || status >= 300) {
        setMigrationStatus(null)
        setMigrationError('Could not load migration status.')
        return
      }
      const parsed = JSON.parse(text) as ApiResponse<MigrationStatus>
      setMigrationStatus(parsed.data)
    } catch (error) {
      setMigrationStatus(null)
      const message = error instanceof Error ? error.message : 'Unknown error'
      setMigrationError(message)
      setResponseText(`Error: ${message}`)
    } finally {
      setIsLoading(false)
    }
  }

  async function applyPendingMigrations() {
    if (
      migrationStatus &&
      !migrationStatus.dirty &&
      migrationStatus.pending_count === 0 &&
      migrationStatus.recorded_version != null
    ) {
      setResponseText('No pending SQL migrations.')
      return
    }

    const pendingCount = migrationStatus?.pending_count
    const confirmed = window.confirm(
      migrationStatus == null
        ? 'Apply any pending SQL migrations? This updates the live database.'
        : pendingCount && pendingCount > 0
          ? `Apply ${pendingCount} pending SQL migration(s)? This updates the live database.`
          : 'Record migration baseline version 8 and check for pending SQL migrations?',
    )
    if (!confirmed) return

    setIsLoading(true)
    setMigrationError('')
    setResponseText('Applying pending SQL migrations…')
    try {
      if (!API_BASE_URL) {
        throw new Error('API base URL is not configured (VITE_API_GATEWAY_URL).')
      }
      const { status, text } = await executeRequest('/v1/database/migrations/apply', 'POST')
      setResponseText(`Apply pending migrations\n${formatResponse(status, text)}`)
      if (status < 200 || status >= 300) {
        setMigrationError('Could not apply pending migrations.')
        return
      }
      const refresh = await executeRequest('/v1/database/migrations/status', 'GET')
      if (refresh.status >= 200 && refresh.status < 300) {
        const parsed = JSON.parse(refresh.text) as ApiResponse<MigrationStatus>
        setMigrationStatus(parsed.data)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      setMigrationError(message)
      setResponseText(`Error: ${message}`)
    } finally {
      setIsLoading(false)
    }
  }

  async function clearOrderData() {
    const confirmed = window.confirm(
      'This removes purchase orders, order confirmations, emails, suppliers, supplier settings, and related rows. Organizations, users, and workflows are kept. Continue?',
    )
    if (!confirmed) return
    setIsLoading(true)
    try {
      await runDatabaseStep('/v1/database/clear-order-data', 'POST', 'Clear order data')
    } catch (error) {
      setResponseText(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  async function resetWorkflow() {
    const workflow = workflows.find((candidate) => candidate.id === resetWorkflowId)
    if (!workflow) return

    const confirmed = window.confirm(
      `Reset the workflow "${workflow.name}"?\n\n` +
        'This removes the order confirmations, purchase orders and emails in the database, ' +
        'the process files in the bucket, and moves handled mail back to the inbox. ' +
        'The purchase orders are reset in the ERP. ' +
        'Workflows, settings and users are kept.' +
        (initiateResetRun ? '\n\nThe workflow is then started, so the mail is fetched again.' : ''),
    )
    if (!confirmed) return

    setIsLoading(true)
    setResetSummary(null)
    try {
      const path = `/v1/workflows/${workflow.id}/reset${initiateResetRun ? '?initiate=true' : ''}`
      const label = initiateResetRun ? 'Reset workflow and start a run' : 'Reset workflow'
      setEndpoint(path)
      setActivePreset('')
      setResponseText(`Running: ${label}…`)
      const { status, text } = await executeRequest(path, 'POST')
      setResponseText(`${label}\n${formatResponse(status, text)}`)
      setResetSummary(summarizeWorkflowReset(status, text, initiateResetRun))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      setResponseText(`Error: ${message}`)
      setResetSummary({ status: 0, askedForRun: initiateResetRun, error: message })
    } finally {
      setIsLoading(false)
    }
  }

  function erpPurchaseOrderPath(suffix = '') {
    const workflowId = erpWorkflowId.trim()
    const orderNumber = encodeURIComponent(erpOrderNumber.trim())
    if (!workflowId || !erpOrderNumber.trim()) {
      throw new Error('Workflow ID and order number are required.')
    }
    return `/v1/workflows/${workflowId}/erp/purchase-orders/${orderNumber}${suffix}`
  }

  async function showERPConfig() {
    setIsLoading(true)
    setResponseText('')
    try {
      const workflowId = erpWorkflowId.trim()
      if (!workflowId) {
        throw new Error('Workflow ID is required.')
      }
      const path = `/v1/workflows/${workflowId}/erp/config`
      setEndpoint(path)
      setActivePreset('')
      await runDatabaseStep(path, 'GET', 'ERP config')
    } catch (error) {
      setResponseText(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  async function fetchPurchaseOrderFromErp() {
    setIsLoading(true)
    try {
      const path = erpPurchaseOrderPath()
      setEndpoint(path)
      setActivePreset('')
      await runDatabaseStep(path, 'GET', 'Fetch purchase order from ERP')
    } catch (error) {
      setResponseText(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  async function resetPurchaseOrderInErp() {
    const orderNumber = erpOrderNumber.trim()
    const confirmed = window.confirm(
      `Reset purchase order "${orderNumber}" in the ERP?\n\n` +
        'This unconfirms the order, clears the supplier order number, merges split rows, and deletes added rows. ' +
        'This only affects the ERP, not the Frontflow database.',
    )
    if (!confirmed) return

    setIsLoading(true)
    try {
      const path = erpPurchaseOrderPath('/reset')
      setEndpoint(path)
      setActivePreset('')
      await runDatabaseStep(path, 'POST', 'Reset purchase order in ERP')
    } catch (error) {
      setResponseText(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <main className="container mx-auto py-12">
      <div className="mx-auto max-w-3xl space-y-6 rounded-panel border border-canvas-border bg-surface p-6 shadow-panel">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Developer tools</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            API requests, SQL migrations, and ERP utilities for development and deployment.
          </p>
        </div>

        {/* SQL migrations */}
        <div className="space-y-3 rounded-lg border border-dashed border-blue-300 bg-blue-50/50 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-800">SQL migrations</p>
            <p className="text-xs text-gray-600">
              Versioned SQL files embedded in the API. Production databases are assumed to have
              migrations 1–8 already applied; the apply step records version 8 in{' '}
              <span className="font-mono">schema_migrations</span> when missing, then runs any
              pending files.
            </p>
          </div>

          {migrationStatus && (
            <div className="space-y-2 rounded-md border border-blue-200 bg-white px-3 py-2 text-xs">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-700">
                <span>
                  Recorded version:{' '}
                  <span className="font-mono font-medium">
                    {migrationStatus.recorded_version ?? 'none'}
                  </span>
                </span>
                <span>
                  Effective version:{' '}
                  <span className="font-mono font-medium">{migrationStatus.effective_version}</span>
                </span>
                <span>
                  Pending:{' '}
                  <span className="font-mono font-medium">{migrationStatus.pending_count}</span>
                </span>
                {migrationStatus.dirty && (
                  <span className="font-medium text-red-700">Dirty — fix before applying</span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b text-gray-500">
                      <th className="py-1 pr-3 font-medium">Version</th>
                      <th className="py-1 pr-3 font-medium">Name</th>
                      <th className="py-1 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {migrationStatus.migrations.map((migration) => (
                      <tr key={migration.version} className="border-b border-gray-100 last:border-0">
                        <td className="py-1 pr-3 font-mono">{migration.version}</td>
                        <td className="py-1 pr-3">{migration.name}</td>
                        <td
                          className={cn(
                            'py-1 font-medium',
                            migration.status === 'applied' ? 'text-green-700' : 'text-amber-700',
                          )}
                        >
                          {migration.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {migrationError && <p className="text-xs text-red-700">{migrationError}</p>}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={checkMigrationStatus}
              disabled={isLoading}
            >
              Check migration status
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={applyPendingMigrations}
              disabled={
                isLoading ||
                migrationStatus?.dirty === true ||
                (migrationStatus != null &&
                  migrationStatus.pending_count === 0 &&
                  migrationStatus.recorded_version != null)
              }
            >
              Apply pending migrations
            </Button>
          </div>
        </div>

        {/* Reset workflow (dev/staging, and FrontFlow in production) */}
        {canResetWorkflow && (
          <div className="space-y-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-gray-800">Reset workflow</p>
              <p className="text-xs text-gray-600">
                Returns one workflow to the state a run expects: clears its run data from the
                database and the bucket, resets its purchase orders in the ERP, and moves handled
                mail back to the inbox. Workflows, settings and users are kept. Only the FrontFlow
                demo organization can be reset.
              </p>
            </div>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-gray-600">Workflow</span>
              <select
                value={resetWorkflowId}
                onChange={(e) => setResetWorkflowId(e.target.value)}
                disabled={isLoading || workflows.length === 0}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select a workflow…</option>
                {workflows.map((workflow) => (
                  <option key={workflow.id} value={workflow.id}>
                    {workflow.name} ({workflow.id})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex cursor-pointer items-start gap-2.5 text-xs text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-gray-900 focus:ring-gray-400"
                checked={initiateResetRun}
                onChange={(e) => setInitiateResetRun(e.target.checked)}
                disabled={isLoading}
              />
              <span>
                Start a run afterwards, so the mail is fetched again (
                <span className="font-mono">?initiate=true</span>). Untick to reset now and trigger
                the run later, in front of the customer.
              </span>
            </label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-amber-600 text-amber-950 hover:bg-amber-50"
              onClick={resetWorkflow}
              disabled={isLoading || !resetWorkflowId}
            >
              {initiateResetRun ? 'Reset workflow and start a run' : 'Reset workflow'}
            </Button>

            {resetSummary && (
              <div className="space-y-1 rounded-md border border-amber-200 bg-white px-3 py-2 text-xs">
                {resetSummary.error ? (
                  <p className="font-medium text-red-700">
                    Failed{resetSummary.status ? ` (${resetSummary.status})` : ''}: {resetSummary.error}
                  </p>
                ) : (
                  <>
                    <p className="text-gray-700">
                      Reset done — mail moved back:{' '}
                      <span className="font-mono font-medium">{resetSummary.mailMovedBack ?? 0}</span>, purchase
                      orders reset:{' '}
                      <span className="font-mono font-medium">
                        {resetSummary.purchaseOrders?.length ?? 0}
                      </span>
                      , bucket objects deleted:{' '}
                      <span className="font-mono font-medium">
                        {resetSummary.bucketObjectsDeleted ?? 0}
                      </span>
                    </p>
                    {resetSummary.askedForRun ? (
                      resetSummary.runStarted ? (
                        <p className="font-medium text-green-700">
                          Run started at{' '}
                          <span className="font-mono">{resetSummary.startBlock || 'the start block'}</span>.
                        </p>
                      ) : (
                        <p className="font-medium text-red-700">
                          A run was requested but did not start. The reset itself completed.
                        </p>
                      )
                    ) : (
                      <p className="text-gray-500">No run was requested.</p>
                    )}
                    {resetSummary.mailMovedBack === 0 && (
                      <p className="text-amber-800">
                        Nothing needed moving: either the mail is already in the inbox, or it sits
                        somewhere the reset does not look (it checks In progress, Handled and Not
                        handled).
                      </p>
                    )}
                  </>
                )}
                {resetSummary.warnings?.map((warning, index) => (
                  <p key={`${index}-${warning}`} className="text-amber-800">
                    Warning: {warning}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Clear order data (dev/staging only) */}
        {canClearOrderData && (
          <div className="space-y-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-gray-800">Clear order data</p>
              <p className="text-xs text-gray-600">
                Removes purchase orders, order confirmations, emails, suppliers, and supplier
                settings. Organizations, users, and workflows are kept. Not available in
                production.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-amber-600 text-amber-950 hover:bg-amber-50"
              onClick={clearOrderData}
              disabled={isLoading}
            >
              Clear order data
            </Button>
          </div>
        )}

        {/* ERP */}
        <div className="space-y-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-800">ERP purchase orders</p>
            <p className="text-xs text-gray-600">
              Read or reset a purchase order in the workflow&apos;s ERP (Monitor). Use the customer order
              number (e.g. mock <span className="font-mono">PO-2026-00001</span>).
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-gray-600">Workflow ID</span>
              <Input
                value={erpWorkflowId}
                onChange={(e) => setErpWorkflowId(e.target.value)}
                placeholder={WF}
                className="font-mono text-sm"
                disabled={isLoading}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-gray-600">Order number</span>
              <Input
                value={erpOrderNumber}
                onChange={(e) => setErpOrderNumber(e.target.value)}
                placeholder={DEFAULT_ERP_ORDER_NUMBER}
                className="font-mono text-sm"
                disabled={isLoading}
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={showERPConfig}
              disabled={isLoading || !erpWorkflowId.trim()}
            >
              ERP config
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={fetchPurchaseOrderFromErp}
              disabled={isLoading || !erpWorkflowId.trim() || !erpOrderNumber.trim()}
            >
              Fetch from ERP
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-red-300 text-red-800 hover:bg-red-50"
              onClick={resetPurchaseOrderInErp}
              disabled={isLoading || !erpWorkflowId.trim() || !erpOrderNumber.trim()}
            >
              Reset order in ERP
            </Button>
          </div>
        </div>

        {/* Preset groups */}
        <div className="space-y-3">
          {PRESETS.map(({ group, endpoints }) => (
            <div key={group}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-gray-500">{group}</p>
              <div className="flex flex-wrap gap-2">
                {endpoints.map(({ label, path }) => (
                  <button
                    key={path}
                    type="button"
                    onClick={() => fireRequest(path)}
                    disabled={isLoading}
                    className={cn(
                      'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                      activePreset === path
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Manual input */}
        <div className="flex gap-2">
          <Input
            value={endpoint}
            onChange={(e) => {
              setEndpoint(e.target.value)
              setActivePreset('')
            }}
            onKeyDown={(e) => e.key === 'Enter' && fireRequest(endpoint)}
            placeholder="/v1/users/me"
            className="font-mono"
          />
          <Button onClick={() => fireRequest(endpoint)} disabled={isLoading || !endpoint.trim()}>
            {isLoading ? 'Loading…' : 'GET'}
          </Button>
        </div>

        {/* Response */}
        <pre className="min-h-52 overflow-auto whitespace-pre-wrap break-words rounded-md border bg-muted/30 p-4 text-sm">
          {responseText || 'Response will appear here.'}
        </pre>
      </div>
    </main>
  )
}
